import { Popover, Typography } from "@material-ui/core";
import { makeThemedStyles } from "@qpr/client-api-base/viewcomponents/utils";
import React from "react";
import clsx from "clsx";
import { ICardElementWithChildren } from "backend/model/model";
import flattenHierarchy from "backend/flattenHierarchy";
import { useClickAway } from "react-use";

const useStyles = makeThemedStyles("SummaryPopoover", (theme: any) => ({
  root: {
    background: "#FFFFFF",
    borderRadius: "1.6em",
    padding: "2.4em 1.6em 2.9em 1.6em",
  },
  paper: {
    fontSize: "1.142em",
    padding: 0,
    borderRadius: "1.6em",
    minWidth: "29.3em",
    width: "auto",
    overflowX: "unset",
    overflowY: "unset",
    boxShadow: "0px 3px 6px rgba(0,0,0,0.2)",
    height: "23.5em",
  },
  item: {
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    "&:first-child": {
      paddingBottom: "1.8em",
      marginBottom: "1.6em",
      borderBottom: "1px solid rgba(234, 236, 242, 1)",
    },
  },
  title: {
    fontSize: "1.2em",
    lineHeight: "1.66em",
    marginBottom: ".8em",
    letterSpacing: "0.05625px",
    color: "rgba(101, 106, 114, 1)",
    textTransform: "uppercase",
  },
  ranges: {
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
  },
  rangeColor: {
    fontSize: "1.2em",
    marginTop: "0.33em",
    lineHeight: "1.25em",
    borderRadius: "1.66em",
    height: "1.916em",
    minWidth: "2.416em",
    boxShadow: "0px 3px 5px rgba(16, 41, 117, 0.08)",
    padding: ".33em 1em",
    background: "rgba(221, 81, 70, 1)",
    color: "rgb(255, 255,255)",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
  },
  rangeTitle: {
    margin: 0,
    fontSize: "1.2em",
    lineHeight: "1.25em",
    color: "rgba(101, 106, 114, 1)",
    fontWeight: 500,
    whiteSpace: "nowrap",
  },
  range: {
    margin: "0 1.4em",
    display: "flex",
    alignItems: "center",
    flexDirection: "column",
    justifyContent: "center",
  },
}));

type RangeItem = {
  name: string;
  color: string;
  count: number;
};

type RangeItemGroup = {
  label: string;
  ranges: RangeItem[];
};

function PopoverContent({ ranges }: { ranges: RangeItemGroup[] }) {
  const classes = useStyles();
  console.log("ranges, ", ranges);
  return (
    <div className={classes.root}>
      {ranges.map((item, index) => (
        <div key={index} className={classes.item}>
          <Typography className={classes.title}>{item.label}</Typography>
          <div className={classes.ranges}>
            {item.ranges.map((range, index) => (
              <div key={range.name} className={clsx(classes.range)}>
                <div className={classes.rangeTitle}>{range.name}</div>
                <div
                  className={classes.rangeColor}
                  style={{ backgroundColor: `#${range.color}` }}
                >
                  {range.count}
                </div>
              </div>
            ))}
          </div>
        </div>
      ))}
    </div>
  );
}

const SummaryPopoover = (
  props: React.PropsWithChildren<{
    items: ICardElementWithChildren[];
    typenames: string[];
    isOpen?: boolean;
    anchor?: HTMLButtonElement | HTMLDivElement | null;
    onClose?: () => void;
    rtl?: boolean;
  }>
) => {
  const classes = useStyles();
  const ref = React.useRef<HTMLElement | null>(null);
  useClickAway(ref, () => {
    if (props.onClose) props.onClose();
  });

  const groups = React.useMemo(() => {
    const list = flattenHierarchy(props.items);
    const allGroups: RangeItemGroup[] = [];
    const getGroup = (name: string) => {
      const r = allGroups.find((n) => n.label === name);
      if (!r) {
        const newGroup: RangeItemGroup = {
          label: name,
          ranges: [],
        };
        allGroups.push(newGroup);
        return newGroup;
      }
      return r;
    };
    return props.typenames.map((typename) => {
      const group = getGroup(typename);
      const findRange = (name: string, color: string) => {
        const r = group.ranges.find((n) => n.name === name);
        if (!r) {
          const newRangeItem: RangeItem = {
            name,
            color: color,
            count: 0,
          };
          group.ranges.push(newRangeItem);
          return newRangeItem;
        }
        return r;
      };

      list
        .filter((i) => i.typename === typename)
        .forEach((i) => {
          if (i.rangecolor && i.rangename) {
            const range = findRange(i.rangename, i.rangecolor);
            range.count += 1;
          } else {
            const nRange = findRange("No data", "000000");
            nRange.count += 1;
          }
        });

      return group;
    });
  }, [props.items]);

  return (
    <Popover
      ref={ref}
      open={!!props.isOpen}
      anchorEl={props.anchor}
      onClose={props.onClose}
      classes={{ paper: classes.paper }}
      anchorOrigin={{
        vertical: "center",
        horizontal: props.rtl ? "left" : "right",
      }}
      transformOrigin={{
        vertical: "center",
        horizontal: "center",
      }}
      dir={props.rtl ? "rtl" : "ltr"}
    >
      <PopoverContent ranges={groups} />
    </Popover>
  );
};

export default SummaryPopoover;
